import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MapPin, ArrowRight } from 'lucide-react';
import { EventCard } from '../components/events/EventCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CITY_DATA = {
  'Madrid': {
    image: 'https://images.pexels.com/photos/919282/pexels-photo-919282.jpeg',
    description: 'La capital de España alberga algunos de los teatros más prestigiosos, incluyendo el Teatro Real y el Teatro de la Zarzuela.'
  },
  'Barcelona': {
    image: 'https://images.pexels.com/photos/14786691/pexels-photo-14786691.jpeg',
    description: 'Ciudad cosmopolita con una rica oferta cultural, destacando el Gran Teatre del Liceu.'
  },
  'Valencia': {
    image: 'https://images.pexels.com/photos/32317216/pexels-photo-32317216.jpeg',
    description: 'El Palau de les Arts Reina Sofía es el epicentro cultural de la ciudad.'
  },
  'Sevilla': {
    image: 'https://images.pexels.com/photos/3290068/pexels-photo-3290068.jpeg',
    description: 'El Teatro de la Maestranza ofrece una programación de ópera y ballet de primer nivel.'
  },
  'Bilbao': {
    image: 'https://images.pexels.com/photos/3753025/pexels-photo-3753025.jpeg',
    description: 'El Palacio Euskalduna es el centro neurálgico de la cultura vasca.'
  },
  'Málaga': {
    image: 'https://images.pexels.com/photos/5282269/pexels-photo-5282269.jpeg',
    description: 'El Teatro Cervantes es el principal espacio escénico de la Costa del Sol.'
  }
};

export const Cities = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [cities, setCities] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const selectedCity = searchParams.get('city');

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    if (selectedCity) {
      fetchEventsByCity(selectedCity);
    } else {
      setEvents([]);
    }
  }, [selectedCity]);

  const fetchCities = async () => {
    try {
      const response = await axios.get(`${API}/cities`);
      setCities(response.data);
    } catch (error) {
      console.error('Error fetching cities:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEventsByCity = async (city) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/events?city=${city}`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectCity = (city) => {
    if (city === selectedCity) {
      setSearchParams({});
    } else {
      setSearchParams({ city });
    }
  };

  return (
    <div className="min-h-screen pt-24" data-testid="cities-page">
      {/* Header */}
      <div className="section-container py-12 border-b border-white/5">
        <span className="text-[var(--primary)] text-xs uppercase tracking-[0.3em] block mb-4">
          Ubicaciones
        </span>
        <h1 className="font-['Playfair_Display'] text-5xl md:text-6xl text-white mb-6">
          Eventos por Ciudad
        </h1>
        <p className="text-white/50 max-w-2xl">
          Encuentra los eventos culturales en tu ciudad o descubre nuevas experiencias 
          en otras localidades de España.
        </p>
      </div>

      {/* Cities Grid */}
      <div className="section-container section-spacing">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {Object.entries(CITY_DATA).map(([city, data]) => (
            <button
              key={city}
              onClick={() => selectCity(city)}
              className={`city-card group text-left ${
                selectedCity === city ? 'ring-2 ring-[var(--primary)]' : ''
              }`}
              data-testid={`city-select-${city.toLowerCase()}`}
            >
              <div 
                className="city-card-image"
                style={{ backgroundImage: `url(${data.image})` }}
              />
              <div className="city-card-overlay" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className={`font-['Playfair_Display'] text-3xl transition-colors ${
                      selectedCity === city ? 'text-[var(--primary)]' : 'text-white group-hover:text-[var(--primary)]'
                    }`}>
                      {city}
                    </h3>
                    <p className="text-white/50 text-sm mt-2 line-clamp-2">
                      {data.description}
                    </p>
                  </div>
                  <div className={`p-2 transition-all ${
                    selectedCity === city 
                      ? 'bg-[var(--primary)] text-black' 
                      : 'bg-white/10 text-white group-hover:bg-[var(--primary)] group-hover:text-black'
                  }`}>
                    <MapPin size={20} />
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Events for Selected City */}
        {selectedCity && (
          <div className="animate-fadeIn" data-testid="city-events">
            <div className="flex items-center justify-between mb-8">
              <div>
                <span className="text-[var(--primary)] text-xs uppercase tracking-[0.3em] block mb-2">
                  Eventos en
                </span>
                <h2 className="font-['Playfair_Display'] text-4xl text-white">
                  {selectedCity}
                </h2>
              </div>
              <span className="text-white/30">
                {events.length} eventos
              </span>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => (
                  <div key={i} className="card-event">
                    <div className="aspect-[4/3] skeleton" />
                    <div className="p-6 space-y-4">
                      <div className="h-6 skeleton w-3/4" />
                      <div className="h-4 skeleton w-full" />
                    </div>
                  </div>
                ))}
              </div>
            ) : events.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {events.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-[var(--surface)] border border-white/5">
                <p className="text-white/50">
                  No hay eventos programados en {selectedCity} actualmente.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Prompt to select city */}
        {!selectedCity && (
          <div className="text-center py-12 bg-[var(--surface)] border border-white/5" data-testid="select-city-prompt">
            <MapPin size={48} className="mx-auto text-[var(--primary)] mb-4" />
            <p className="text-white/70 text-lg mb-2">
              Selecciona una ciudad
            </p>
            <p className="text-white/30">
              Haz clic en una ciudad para ver sus eventos programados
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cities;
